<script lang="ts">
import { marked, Lexer, type TokenizerObject, type Tokens } from 'marked'
import sanitizeHtml from 'sanitize-html'

export default defineComponent({
  props: {
    content: {
      type: String,
      required: true,
    },
    container: {
      type: String,
      default: 'div',
    },
  },

  setup(props) {
    const localePath = useLocalePath()

    const preprocess = (content: string) => {
      if (!content) return ''

      // Convert [lang=en](word) to <span lang="xx">word</span>
      let processedContent = content?.replace(
        /(?:\[lang=([a-z]{2})\]\((.*?)\)|\\\[lang=([a-z]{2})\\\]\((.*?)\))/gi,
        (match, p1, p2, p3, p4) => {
          const lang = p1 || p3
          const word = p2 || p4
          return `<span lang="${lang}">${word}</span>`
        }
      )

      // Handle links that start with "/"
      processedContent = processedContent?.replace(
        /\[([^\]]+)\]\((\/[^\)]+)\)/g,
        (match, text, link) => {
          return `<a href="${localePath(link)}">${text}</a>`
        }
      )

      // Convert newLine chars into <br/> tags to preserve old data entry
      processedContent = processedContent.replace(/\\n/g, '<br/>')

      // Convert character surrounded by 2 spaces and a dash to underlined text
      processedContent = processedContent.replace(/__(.*?)__/g, '<u>$1</u>')

      return processedContent
    }

    // The lexer is needed to parse inline tokens in the custom tokenizer
    const lexer = new Lexer()
    /** Add in this object here any other logic for matching custom tokens as a function.
     * @see https://marked.js.org/using_pro#tokenizer
     */
    const customTokenizer: TokenizerObject = {
      heading: (src: string) => {
        // Add custom logic form matching heading formatted as "##Heading" (without space)
        const match = src.match(/^(#{1,6})([^#\s\n][^\n]*)(?:\n+|$)/) // Match heading with no space or newline immediately after the # and all characters until the end of the line
        if (match) {
          const headingLevel = match[1].length
          const text = match[2]
          return {
            type: 'heading',
            depth: headingLevel,
            raw: match[0],
            text,
            tokens: lexer.inlineTokens(text), // Further parse for inline tokens (links, strong, etc.)
          }
        }
        // If there is no match, return false and fallback to default marked tokenizer (for cases such as "## Heading")
        return false
      },
    }

    const md = ref(props?.content)

    // Add preprocessor to marked
    marked.use({ hooks: { preprocess }, tokenizer: customTokenizer })
    const html = marked(md?.value) as string // casted as string because marked runs synchronously by default

    return () =>
      h(props?.container ?? 'div', {
        innerHTML: sanitizeHtml(html),
      })
  },
})
</script>
